import React, { Fragment, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Table,
} from "reactstrap";
import * as XLSX from "xlsx";
import Papa from "papaparse";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";
import { Breadcrumbs } from "../../../AbstractElements";
import API from "../../../api/baseApi";
import ProgramSlugModal from "../../Common/ProgramSlugModal";

// Fields accepted by pl-server's POST /signupUser/admin/bulkDirectCreate, which creates a
// fully paid + detail-filled Customer directly (isActive stays false until the customer sets
// their password via the normal activation email) - skips OTP + payment self-signup entirely.
// This is a separate feature from the existing B2B/B2C bulk-upload tabs on the Customers page -
// it does not read, write, or otherwise touch that flow or its data.
// uniqueRegID is intentionally not mappable here - pl-server generates it automatically
// (CUST_xxxxxxxx) for every row that doesn't have one.
const DIRECT_CREATE_FIELDS = [
  { key: "first_name", label: "First Name", required: true },
  { key: "last_name", label: "Last Name", required: false },
  { key: "email", label: "Email", required: true },
  { key: "phone", label: "Phone", required: true },
  { key: "program_slug", label: "Program Slug", required: true },
  { key: "gender", label: "Gender (male/female/others)", required: false },
  { key: "dateofbirth", label: "Date of Birth (YYYY-MM-DD)", required: false },
  { key: "address", label: "Address", required: false },
  { key: "pincode", label: "Pincode", required: false },
  { key: "country_id", label: "Country ID", required: false },
  { key: "state_id", label: "State ID", required: false },
  { key: "city_id", label: "City ID", required: false },
];

const parseExcelFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });

        if (!jsonData?.length) {
          reject(new Error("Excel file is empty"));
          return;
        }

        const headers = jsonData[0].map((h) => String(h || "").trim()).filter(Boolean);
        const rows = jsonData
          .slice(1)
          .map((row) => {
            const obj = {};
            headers.forEach((header, index) => {
              obj[header] = row[index] !== undefined ? String(row[index] || "").trim() : "";
            });
            return obj;
          })
          .filter((row) => Object.values(row).some((v) => v));

        resolve({ headers, rows });
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
};

const parseCSVFile = (file) => {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transform: (value) => (typeof value === "string" ? value.trim() : value),
      complete: (results) => {
        if (results?.errors?.length > 0 && results?.data?.length === 0) {
          reject(new Error("Error parsing CSV file"));
          return;
        }
        const headers = Object.keys(results.data[0] || {});
        resolve({ headers, rows: results.data });
      },
      error: reject,
    });
  });
};

const DirectCustomerUpload = () => {
  const inputRef = useRef(null);

  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [mappingErrors, setMappingErrors] = useState({});
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [results, setResults] = useState(null);
  const [slugModalOpen, setSlugModalOpen] = useState(false);

  const resetUpload = () => {
    setHeaders([]);
    setRows([]);
    setMapping({});
    setMappingErrors({});
    setShowMappingModal(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleFileChange = async (event) => {
    const selected = event.target.files[0];
    if (!selected) return;

    if (selected.size > 5 * 1024 * 1024) {
      toast.error("File size should be less than 5MB");
      resetUpload();
      return;
    }

    const isCSV = selected.type === "text/csv" || selected.name.endsWith(".csv");
    const isExcel =
      selected.name.endsWith(".xlsx") ||
      selected.name.endsWith(".xls") ||
      selected.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      selected.type === "application/vnd.ms-excel";

    if (!isCSV && !isExcel) {
      toast.error("Please upload a valid CSV or Excel file (.csv, .xlsx, .xls)");
      resetUpload();
      return;
    }

    try {
      const { headers: parsedHeaders, rows: parsedRows } = isCSV
        ? await parseCSVFile(selected)
        : await parseExcelFile(selected);

      if (parsedRows.length > 200) {
        toast.error(
          "Please upload a file with 200 records or fewer at a time - this creates accounts " +
            "one by one in real time, so larger batches risk timing out mid-upload.",
        );
        resetUpload();
        return;
      }

      setHeaders(parsedHeaders);
      setRows(parsedRows);

      const initialMapping = {};
      DIRECT_CREATE_FIELDS.forEach((field) => {
        initialMapping[field.key] = parsedHeaders.includes(field.key) ? field.key : "";
      });
      setMapping(initialMapping);
      setMappingErrors({});
      setResults(null);
      setShowMappingModal(true);
    } catch (err) {
      toast.error(`Error parsing file: ${err.message}`);
      resetUpload();
    }
  };

  const availableHeadersFor = (currentField) => {
    const currentValue = mapping[currentField] || "";
    const usedElsewhere = Object.entries(mapping)
      .filter(([key, header]) => key !== currentField && header)
      .map(([, header]) => header);
    return headers.filter((h) => h === currentValue || !usedElsewhere.includes(h));
  };

  const handleMappingChange = (field, header) => {
    setMapping((prev) => ({ ...prev, [field]: header }));
    if (mappingErrors[field]) {
      setMappingErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const validateMapping = () => {
    const errors = {};
    DIRECT_CREATE_FIELDS.filter((f) => f.required).forEach((field) => {
      if (!mapping[field.key]) {
        errors[field.key] = `${field.label} is required but not mapped`;
      }
    });
    setMappingErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateMapping()) {
      toast.error("Please map all required fields before uploading.");
      return;
    }

    const customers = rows.map((row) => {
      const mapped = {};
      DIRECT_CREATE_FIELDS.forEach((field) => {
        const header = mapping[field.key];
        if (header && row[header] !== undefined && row[header] !== "") {
          mapped[field.key] = row[header];
        }
      });
      return mapped;
    });

    setIsSubmitting(true);
    try {
      // Each row is created one at a time server-side (not streamed), and can involve an
      // external provisioning call - a full 200-row batch can legitimately take minutes.
      const response = await API.post(
        "/signupUser/admin/bulkDirectCreate",
        { customers },
        { timeout: 10 * 60 * 1000 },
      );

      const data = response?.data;
      setResults(data);
      setShowMappingModal(false);

      if (data?.failed > 0) {
        toast.warn(`${data.succeeded} created, ${data.failed} failed - see results below.`);
      } else {
        toast.success(`All ${data?.succeeded ?? customers.length} customers created successfully.`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || `Upload failed: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadTemplate = () => {
    const csvContent = DIRECT_CREATE_FIELDS.map((f) => f.key).join(",");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
    saveAs(blob, "direct-create-template.csv");
  };

  const handleDownloadResults = () => {
    if (!results?.results?.length) return;
    const cols = ["success", "message", ...DIRECT_CREATE_FIELDS.map((f) => f.key)];
    const csvRows = [cols.join(",")];
    results.results.forEach((r) => {
      const line = cols.map((c) => {
        if (c === "success") return r.success ? "Yes" : "No";
        if (c === "message") return `"${(r.message || "").replace(/"/g, '""')}"`;
        return r.input?.[c] ?? "";
      });
      csvRows.push(line.join(","));
    });
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8" });
    saveAs(blob, "direct-create-results.csv");
  };

  return (
    <Fragment>
      <Breadcrumbs
        mainTitle="Direct Customer Upload"
        parent="Customers"
        parentPath="direct-customer-upload"
        title="Direct Customer Upload"
      />
      <Container fluid={true}>
        <Row>
          <Col sm="12">
            <Card>
              <CardBody>
                <Alert color="info" className="bg-primary bg-opacity-25">
                  Creates fully paid, detail-filled customer accounts directly from an Excel file - no
                  OTP or payment step. Each customer still gets the normal activation email to set their
                  password. This is separate from the existing bulk-upload flow on the Customers page.
                </Alert>

                <FormGroup>
                  <Label for="directCreateFile">Upload CSV or Excel File</Label>
                  <Input
                    type="file"
                    id="directCreateFile"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileChange}
                    innerRef={inputRef}
                  />
                  <small className="form-text text-muted">
                    Supported formats: .csv, .xlsx, .xls. Max file size: 5MB. Max records: 200.
                    Each row's Program Slug determines which Client the customer belongs to.
                  </small>
                </FormGroup>

                <Button color="secondary" onClick={handleDownloadTemplate}>
                  Download Template
                </Button>{" "}
                <Button color="outline-secondary" onClick={() => setSlugModalOpen(true)}>
                  📋 Programs &amp; Slugs
                </Button>
                <ProgramSlugModal isOpen={slugModalOpen} toggle={() => setSlugModalOpen(false)} />

                {results && (
                  <div className="mt-4">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <h5 className="mb-0">
                        Results: {results.succeeded} succeeded, {results.failed} failed of{" "}
                        {results.total}
                      </h5>
                      <Button
                        size="sm"
                        color="secondary"
                        style={{ marginLeft: 8 }}
                        onClick={handleDownloadResults}
                      >
                        Download Results CSV
                      </Button>
                    </div>
                    <div style={{ maxHeight: "50vh", overflowY: "auto" }}>
                      <Table responsive striped size="sm">
                        <thead>
                          <tr>
                            <th>Status</th>
                            <th>Email</th>
                            <th>Phone</th>
                            <th>Unique Reg ID</th>
                            <th>Message</th>
                          </tr>
                        </thead>
                        <tbody>
                          {results.results.map((r, idx) => (
                            <tr key={idx}>
                              <td>
                                <span
                                  className={`badge ${r.success ? "badge-success" : "badge-danger"}`}
                                >
                                  {r.success ? "Created" : "Failed"}
                                </span>
                              </td>
                              <td>{r.input?.email}</td>
                              <td>{r.input?.phone}</td>
                              <td>{r.input?.uniqueRegID}</td>
                              <td>{r.success ? r.message : r.message || "Unknown error"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal isOpen={showMappingModal} toggle={resetUpload} size="xl">
        <ModalHeader toggle={resetUpload}>Map Fields to Database</ModalHeader>
        <ModalBody>
          <Alert color="info" className="bg-primary bg-opacity-25">
            Select which column from your uploaded file maps to each field. Required fields are marked
            with <span className="text-danger">*</span>.
          </Alert>
          <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
            <Table responsive striped>
              <thead>
                <tr>
                  <th>Field</th>
                  <th>Select Uploaded Column</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {DIRECT_CREATE_FIELDS.map((field) => {
                  const mappedHeader = mapping[field.key] || "";
                  const isMapped = mappedHeader !== "";
                  const hasError = mappingErrors[field.key];

                  return (
                    <tr key={field.key} style={field.required ? { backgroundColor: "#fff5f5" } : {}}>
                      <td>
                        <strong>
                          {field.label}
                          {field.required && <span className="text-danger ml-1">*</span>}
                        </strong>
                        <br />
                        <small className="text-muted">({field.key})</small>
                      </td>
                      <td>
                        <Input
                          type="select"
                          value={mappedHeader}
                          onChange={(e) => handleMappingChange(field.key, e.target.value)}
                          className={hasError || (field.required && !isMapped) ? "border-danger" : ""}
                        >
                          <option value="">-- Select Uploaded Field --</option>
                          {availableHeadersFor(field.key).map((h) => (
                            <option key={h} value={h}>
                              {h}
                            </option>
                          ))}
                        </Input>
                        {hasError && <small className="text-danger d-block mt-1">{hasError}</small>}
                      </td>
                      <td>
                        <span
                          className={`badge ${
                            field.required
                              ? isMapped
                                ? "badge-success"
                                : "badge-danger"
                              : isMapped
                                ? "badge-info"
                                : "badge-secondary"
                          }`}
                        >
                          {isMapped ? "Mapped" : field.required ? "Required" : "Optional"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" disabled={isSubmitting} onClick={resetUpload}>
            Cancel
          </Button>
          <Button color="primary" disabled={isSubmitting} onClick={handleSubmit}>
            {isSubmitting ? "Creating..." : `Create ${rows.length} Customers`}
          </Button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default DirectCustomerUpload;
